import * as THREE from 'three'

export const GSPLAT_WEATHER_MIN_DENSITY = 0.1
export const GSPLAT_WEATHER_MAX_HALF_CELLS = 128

export interface GSplatWeatherGridConfig {
  readonly extents: THREE.Vector3
  readonly density: number
}

export interface GSplatWeatherTextureLimit {
  readonly name: string
  readonly actual: number
  readonly limit: number
}

export class GSplatWeatherPlanError extends Error {
  readonly code = 'GSPLAT_WEATHER_PLAN_INVALID'
  readonly limit?: GSplatWeatherTextureLimit

  constructor(message: string, limit?: GSplatWeatherTextureLimit) {
    super(message)
    this.name = 'GSplatWeatherPlanError'
    this.limit = limit
  }
}

export interface GSplatWeatherPlan {
  readonly config: GSplatWeatherGridConfig
  readonly cellSize: number
  readonly halfCells: readonly [number, number, number]
  readonly dimensions: readonly [number, number, number]
  readonly count: number
  readonly data: Uint8Array
  readonly centers: Float32Array
  readonly aabb: THREE.Box3
  readonly baseCell: readonly [number, number, number]
}

/**
 * Build deterministic RGBA8 cell data and static half-cell centers.
 *
 * The dimension on each axis is `2 * halfCells`, so the maximum supported
 * half-grid (128) still fits one unsigned byte per axis. Indexing is x→y→z
 * with z as the innermost loop, matching the upstream nested-cell order.
 */
export function buildGSplatWeatherPlan(
  config: GSplatWeatherGridConfig,
  options: {
    readonly maxSplats?: number
    readonly maxBufferSize?: number
    readonly basePosition?: THREE.Vector3
  } = {}
): GSplatWeatherPlan {
  const extents = clonePositiveVector(config.extents, 'extents')
  const density = finitePositive(config.density, 'density')
  const effectiveDensity = Math.max(density, GSPLAT_WEATHER_MIN_DENSITY)
  const cellSize = 1 / effectiveDensity
  const halfCells = Object.freeze([
    clampHalfCells(Math.floor(extents.x * effectiveDensity)),
    clampHalfCells(Math.floor(extents.y * effectiveDensity)),
    clampHalfCells(Math.floor(extents.z * effectiveDensity)),
  ] as [number, number, number])
  const dimensions = Object.freeze([halfCells[0] * 2, halfCells[1] * 2, halfCells[2] * 2] as [
    number,
    number,
    number,
  ])
  const count = dimensions[0] * dimensions[1] * dimensions[2]
  assertLimit('count', count, options.maxSplats)

  const bytes = count * 4
  assertLimit('rgba8Bytes', bytes, options.maxBufferSize)
  const data = new Uint8Array(bytes)
  const centers = new Float32Array(count * 3)
  let index = 0
  for (let x = 0; x < dimensions[0]; x += 1) {
    for (let y = 0; y < dimensions[1]; y += 1) {
      for (let z = 0; z < dimensions[2]; z += 1) {
        const dataOffset = index * 4
        data[dataOffset] = x
        data[dataOffset + 1] = y
        data[dataOffset + 2] = z
        data[dataOffset + 3] = 0

        const centerOffset = index * 3
        centers[centerOffset] = (x - halfCells[0] + 0.5) * cellSize
        centers[centerOffset + 1] = (y - halfCells[1] + 0.5) * cellSize
        centers[centerOffset + 2] = (z - halfCells[2] + 0.5) * cellSize
        index += 1
      }
    }
  }

  const extent = new THREE.Vector3(
    halfCells[0] * cellSize,
    halfCells[1] * cellSize,
    halfCells[2] * cellSize
  )
  const aabb = new THREE.Box3(extent.clone().multiplyScalar(-1), extent)
  const basePosition = options.basePosition ?? new THREE.Vector3()
  const baseCell = Object.freeze([
    floorCell(basePosition.x, cellSize),
    floorCell(basePosition.y, cellSize),
    floorCell(basePosition.z, cellSize),
  ] as [number, number, number])

  return Object.freeze({
    config: Object.freeze({ extents, density }),
    cellSize,
    halfCells,
    dimensions,
    count,
    data,
    centers,
    aabb,
    baseCell,
  })
}

export function snapGSplatWeatherBaseCell(
  position: THREE.Vector3,
  cellSize: number
): readonly [number, number, number] {
  if (!Number.isFinite(cellSize) || cellSize <= 0) {
    throw new GSplatWeatherPlanError(`Weather cellSize 非法: ${cellSize}`)
  }
  if (![position.x, position.y, position.z].every(Number.isFinite)) {
    throw new GSplatWeatherPlanError('Weather base position 必须是有限坐标')
  }
  return Object.freeze([
    floorCell(position.x, cellSize),
    floorCell(position.y, cellSize),
    floorCell(position.z, cellSize),
  ] as [number, number, number])
}

function clonePositiveVector(value: THREE.Vector3, name: string): THREE.Vector3 {
  if (!value || ![value.x, value.y, value.z].every(Number.isFinite)) {
    throw new GSplatWeatherPlanError(`Weather ${name} 必须是有限三维向量`)
  }
  if (value.x <= 0 || value.y <= 0 || value.z <= 0) {
    throw new GSplatWeatherPlanError(`Weather ${name} 必须为正数`)
  }
  return value.clone()
}

function finitePositive(value: number, name: string): number {
  if (!Number.isFinite(value) || value <= 0) {
    throw new GSplatWeatherPlanError(`Weather ${name} 必须为有限正数: ${value}`)
  }
  return value
}

function clampHalfCells(value: number): number {
  return Math.min(GSPLAT_WEATHER_MAX_HALF_CELLS, Math.max(1, value))
}

function floorCell(value: number, cellSize: number): number {
  return Math.floor(value / cellSize)
}

function assertLimit(name: string, actual: number, limit: number | undefined): void {
  if (limit === undefined) return
  if (!Number.isSafeInteger(limit) || limit < 0) {
    throw new GSplatWeatherPlanError(`Weather ${name} limit 非法: ${limit}`)
  }
  if (actual > limit) {
    throw new GSplatWeatherPlanError(`Weather ${name} 超过限制: actual=${actual}, limit=${limit}`, {
      name,
      actual,
      limit,
    })
  }
}
